/**
 * A world pose as the Transform that puts an entity there under its parent:
 * the parent's world inverse times the pose, as Unreal's attach and gizmo
 * drags compute it. Shared by reparenting (`entity.move`), the transform
 * gizmo and camera piloting, so all three write Transforms the same way.
 */
import { quatToEulerDegClosest } from "../core/math/euler";
import { mat4Decompose, mat4Invert, mat4Multiply } from "../core/math/mat4";
import type { Mat4 } from "../core/math/mat4";
import { vec3Round } from "../core/math/vec3";
import type { Vec3 } from "../core/math/vec3";

/** Decimal places kept in a computed Transform: sub-micrometer, and no float noise in the Inspector. */
export const TRANSFORM_PRECISION = 5;

export interface LocalTransform {
  readonly position: Vec3;
  /** Euler degrees, as Transform stores them. */
  readonly rotation: Vec3;
  readonly scale: Vec3;
}

/**
 * The Transform for `world` under a parent at `parentWorld` (null at the top
 * level, or a parent with no inverse, such as one scaled to zero). Rotation
 * stays continuous with `previousRotation`, so a small turn never flips the
 * Euler angles.
 */
export function localTransformAt(
  world: Mat4,
  parentWorld: Mat4 | null,
  previousRotation: Vec3,
): LocalTransform {
  const inverse = parentWorld && mat4Invert(parentWorld);
  const local = mat4Decompose(inverse ? mat4Multiply(inverse, world) : world);
  const rotation = quatToEulerDegClosest(local.rotation, previousRotation);
  return {
    position: vec3Round(local.position, TRANSFORM_PRECISION),
    rotation: vec3Round(rotation, TRANSFORM_PRECISION),
    scale: vec3Round(local.scale, TRANSFORM_PRECISION),
  };
}
